/**
 * Cognito OAuth 2.0 utilities for V2 Enterprise Auth flow.
 *
 * This implements the Authorization Code + PKCE flow:
 * 1. Frontend redirects to Cognito Hosted UI with PKCE code_challenge
 * 2. User authenticates via SAML/OAuth IdP
 * 3. Cognito redirects back with authorization code
 * 4. Frontend sends code + code_verifier to backend
 * 5. Backend exchanges code for tokens
 */

import { generateAndStorePKCE, retrieveCodeVerifier } from './pkce';
import { getApiHost, getEnterpriseSsoHost } from './ssoReferrer';

// Cognito configuration - single prod pool for all environments
const getCognitoConfig = () => {
  return {
    domain: 'stackline-enterprise-sso-signin.auth.us-west-2.amazoncognito.com',
    clientId: '5ktrvofvdidmkmoku14jmkff81',
    userPoolId: 'us-west-2_op1uPQUYe',
    scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
  };
};

const COGNITO_CONFIG = getCognitoConfig();

/**
 * Get the redirect URI based on current environment.
 * Always callbacks to enterprise-sso (the central auth page).
 * Must match exactly what's registered in Cognito User Pool callback URLs.
 */
export function getRedirectUri(): string {
  return `https://${getEnterpriseSsoHost()}/auth/callback`;
}

/**
 * Build the Cognito authorization URL with PKCE.
 */
export async function buildAuthorizationUrl(
  identityProvider: string,
  state?: Record<string, any>
): Promise<string> {
  const { codeChallenge } = await generateAndStorePKCE();

  const redirectUri = getRedirectUri();
  const stateParam = state ? btoa(JSON.stringify(state)) : '';

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: COGNITO_CONFIG.clientId,
    redirect_uri: redirectUri,
    scope: COGNITO_CONFIG.scopes.join(' '),
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    identity_provider: identityProvider,
  });

  if (stateParam) {
    params.set('state', stateParam);
  }

  return `https://${COGNITO_CONFIG.domain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Redirect to Cognito Hosted UI for federated authentication.
 */
export async function redirectToCognito(
  identityProvider: string,
  state?: Record<string, any>
): Promise<void> {
  const url = await buildAuthorizationUrl(identityProvider, state);
  window.location.href = url;
}

/**
 * Parse the callback URL to extract authorization code and state.
 */
export function parseCallbackParams(): {
  code: string | null;
  state: Record<string, any> | null;
  error: string | null;
  errorDescription: string | null;
} {
  const params = new URLSearchParams(window.location.search);

  const code = params.get('code');
  const stateParam = params.get('state');
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  let state: Record<string, any> | null = null;
  if (stateParam) {
    try {
      state = JSON.parse(atob(stateParam));
    } catch (e) {
      console.warn('Failed to parse state parameter:', e);
    }
  }

  return { code, state, error, errorDescription };
}

/**
 * Get the stored code verifier for token exchange.
 */
export function getStoredCodeVerifier(): string | null {
  return retrieveCodeVerifier();
}

/**
 * Exchange authorization code for tokens via backend.
 * @param intendedEmail - The email the user entered before federated redirect (needed for superuser aliases)
 */
export async function exchangeCodeForTokens(
  authCode: string,
  codeVerifier: string,
  referrer?: string,
  intendedEmail?: string
): Promise<any> {
  const redirectUri = getRedirectUri();

  const body: Record<string, string> = {
    authCode,
    codeVerifier,
    redirectUri,
  };
  if (referrer) body.referrer = referrer;
  if (intendedEmail) body.intendedEmail = intendedEmail;

  const response = await fetch(`https://${getApiHost()}/api/auth/v2/login/federated`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error_description || 'Token exchange failed');
  }

  return response.json();
}

